import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Box, Paper, TableContainer, LinearProgress, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from "react-router";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import SearchField from '../../../components/search/Search';
import { useBeneficiaries } from './useBeneficiaries';
import ErrorNotification from '../../../components/ErrorNotification/ErrorNotification';
import api from "../../../services/api";
import { useNotifier } from "muinotify";
import { exportToExcel } from '../../../components/export/exportToExcel';
import { exportToPdf } from '../../../components/export/exportToPdf';
import { exportToCsv } from '../../../components/export/exportToCsv';
import BeneficiariesDetails from './BeneficiariesDetails';

const Beneficiaries = () => {
  const { notifier } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const { beneficiaries, isLoading, error, mutate } = useBeneficiaries(searchQuery);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState(null);

  const URL = `expense-beneficiary`;
  const NAV_PATH = `beneficiaries`;

  const headerNames = {
    eb_company_name: 'Εταιρεία',
    eb_vat_no: 'Α.Φ.Μ',
    eb_address: 'Διεύθυνση',
    eb_phone: 'Αρ. Τηλεφώνου',
    eb_email: 'Email',
    eb_payment_terms: 'Μέρες πληρωμής'
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  function getRowId(row) {
    return row.id;
  }

  const handleClear = () => {
    setSearchQuery("");
    mutate();
  };

  let navigate = useNavigate();

  const handlePanelClose = () => {
    setSelectedBeneficiaries(null);
  };

  const handleView = (beneficiaries) => {
    setSelectedBeneficiaries(beneficiaries);
  };

  const handleEdit = (id) => {
    navigate(`/${NAV_PATH}/update/${id}`);
  };

  const handleAdd = () => {
    navigate(`/${NAV_PATH}/create/`);
  };

  const handleDelete = useCallback((id) => {
    confirmAlert({
      title: 'Επιβεβαιώστε τη διαγραφή',
      message: 'Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτήν την εγγραφή;',
      buttons: [
        {
          label: 'Ναι',
          onClick: async () => {
            try {
              await api.delete(`/${URL}/${id}`);
              notifier("Η παραγωγή διαγράφηκε με επιτυχία.", { variant: "success" });
              await mutate();
            } catch (err) {
              notifier("Αποτυχία διαγραφής παραγωγής. Error: " + err.message, { variant: "error" });
            }
          }
        },
        {
          label: 'Οχι',
        }
      ]
    });
  }, [mutate, notifier]);

  const columnsToExtract = [
    { field: 'eb_company_name', headerName: 'Συναλλασσόμενος' },
    { field: 'eb_vat_no', headerName: 'Α.Φ.Μ' },
    { field: 'eb_address', headerName: 'Διεύθυνση' },
    { field: 'eb_phone', headerName: 'Αρ. Τηλεφώνου' },
    { field: 'eb_email', headerName: 'Email' },
    { field: 'eb_payment_terms', headerName: 'Μέρες πληρωμής' },
  ];

  const handleExportToExcel = () => {
    exportToExcel(
      beneficiaries,
      columnsToExtract,
      `Συναλλασσόμενοι`
    );
  };

  const handleExportToPdf = () => {
    exportToPdf(
      beneficiaries,
      columnsToExtract,
      `Συναλλασσόμενοι`
    );
  };

  const handleExportToCsv = () => {
    exportToCsv(
      beneficiaries,
      columnsToExtract,
      `Συναλλασσόμενοι`
    );
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 1, headerAlign: 'center', },
    { field: 'eb_company_name', headerName: 'Συναλλασσόμενος', flex: 1, headerAlign: 'center', },
    { field: 'eb_vat_no', headerName: 'Α.Φ.Μ', flex: 1, headerAlign: 'center', },
    { field: 'eb_address', headerName: 'Διεύθυνση', flex: 1, headerAlign: 'center', },
    { field: 'eb_phone', headerName: 'Αρ. Τηλεφώνου', flex: 1, headerAlign: 'center', },
    { field: 'eb_email', headerName: 'Email', flex: 1, headerAlign: 'center', },
    { field: 'eb_payment_terms', headerName: 'Μέρες πληρωμής', flex: 1, headerAlign: 'center', align: 'ceneter', },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      headerName: "Actions",
      getActions: (params) => [
        <GridActionsCellItem color="primary" icon={<EditOutlinedIcon />} label="Edit" onClick={() => handleEdit(params.row.id)} />,
        <GridActionsCellItem color="secondary" icon={<DeleteOutlineOutlinedIcon />} label="Delete" onClick={() => handleDelete(params.row.id)} />,
      ],
    },
  ];

  return (
    <Box m="20px" >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Beneficiaries" module="Productions" subtitle="List of beneficiaries" />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={selectedBeneficiaries ? 8 : 12}>

          <FormListToolbar
            handleAdd={handleAdd}
            handleExportToExcel={handleExportToExcel}
            handleExportToPdf={handleExportToPdf}
            handleExportToCsv={handleExportToCsv}
          />
          <Box
            m="8px 0 0 0"
            height="80vh"
          >
            <Paper elevation={2} style={{ padding: '1rem', height: '100%', width: '100%' }}>
              <SearchField searchQuery={searchQuery} handleSearchChange={handleSearchChange} handleClear={handleClear} ml={2} mr={2} />

              {isLoading && <LinearProgress />}

              {error && <ErrorNotification ml={2} mr={2} error={error} />}

              <TableContainer style={{ height: '87%', width: '100%' }}>
                <DataGrid
                  rows={beneficiaries || []}
                  columns={columns}
                  getRowId={getRowId}
                  columnVisibilityModel={{
                    id: false,
                  }}
                  onRowClick={(params) => setSelectedBeneficiaries(params.row)} // Set selected client on row click
                  sx={{
                    '& .MuiDataGrid-columnHeader': {
                      borderRight: '1px solid #ddd',
                    },
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                      borderRight: '1px solid #ddd',
                      borderBottom: 'none',
                    },
                    '& .MuiDataGrid-columnsContainer': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid #e0e0e0',
                    },
                    '& .MuiTablePagination-root': {
                      color: '#000',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                  }}
                />
              </TableContainer>
            </Paper>
          </Box>
        </Grid>
        {selectedBeneficiaries && (
          <Grid item xs={4}>
            <BeneficiariesDetails
              beneficiaries={selectedBeneficiaries}
              headerNames={headerNames}
              onClose={handlePanelClose} />
          </Grid>
        )}
      </Grid>
    </Box>

  );
};

export default Beneficiaries;